import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  ClinicRetentionSummary,
  MetricsSummary,
  ProfesionalesMetrics,
  PvpMetrics,
  ReportFormat,
  Sede,
} from '@/application/metrics';
import { persistReportScope } from '@/application/metrics/metricsScope';
import { breadcrumbHref } from '@/application/navigation/breadcrumbScope';
import {
  downloadSummaryReport,
  getMetricsSummary,
  getOwnerPvpRetentionDashboard,
  getPacientesNoVolvieronResumen,
  getProfesionalesMetrics,
  getPvpMetrics,
  getSedes,
} from '@/infrastructure/http/metricsApi';
import {
  buildAttentionHref,
  canAccessAttentionCta,
  resolveAccessibleAttentionCta,
} from '@/application/metrics/attentionCta';
import { buildHubTopSignals, type HubTopSignal } from '@/application/metrics/clinicHubTopSignals';
import { canAccessClinicHub, firstVisibleHousePath } from '@/application/navigation/menuHouses';
import { canExportReports } from '@/application/permissions/canExportReports';
import { ClinicHubFinancesPanel } from '@/presentation/components/domain/ClinicHubFinancesPanel';
import { ClinicHubPatientsPanel } from '@/presentation/components/domain/ClinicHubPatientsPanel';
import { ClinicHubProfessionalsPanel } from '@/presentation/components/domain/ClinicHubProfessionalsPanel';
import { AccessDenied } from '@/presentation/components/common/AccessDenied';
import { Breadcrumbs } from '@/presentation/components/common/Breadcrumbs';
import { PageSkeleton } from '@/presentation/components/common/PageSkeleton';
import { PanelPageState } from '@/presentation/components/common/PanelPageState';
import { DomainScopeBar } from '@/presentation/components/domain/DomainScopeBar';
import { DomainTabs } from '@/presentation/components/domain/DomainTabs';
import { ExportButtons } from '@/presentation/components/metrics/ExportButtons';
import { MetricKpiCard, MetricPrimaryStrip } from '@/presentation/components/metrics';
import { isMetricDefinitionId } from '@/application/metrics/metricDefinitions';
import { MetricsState } from '@/presentation/components/metrics/MetricsState';
import { formatInteger, formatPercent } from '@/presentation/components/metrics/metricFormatters';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { useClinicNames } from '@/presentation/hooks/useClinicNames';
import { useGlobalPeriod } from '@/presentation/hooks/useGlobalPeriod';
import { formatMoneyCompact, relativeChangePct } from '@/presentation/utils/metricDeltas';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';

type ClinicTab = 'overview' | 'professionals' | 'patients' | 'finances';
type SignalTone = 'up' | 'down' | 'flat';
type CoverBranchStatus = 'ok' | 'attention' | 'review';
type CoverTopSignal = HubTopSignal & {
  title: string;
  body: string;
  ctaLabel: string;
  href: string;
};

function toneFromHigherBetter(delta: number | null, flatBand = 1.5): SignalTone {
  if (delta === null || Math.abs(delta) < flatBand) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

function toneFromLowerBetter(delta: number | null, flatBand = 0.8): SignalTone {
  if (delta === null || Math.abs(delta) < flatBand) return 'flat';
  return delta < 0 ? 'up' : 'down';
}

function toneClass(tone: SignalTone): string {
  if (tone === 'up') return 'ds-tone-up';
  if (tone === 'down') return 'ds-tone-down';
  return 'ds-tone-flat';
}

function formatSigned(locale: string, value: number): string {
  const abs = Math.abs(value).toLocaleString(locale, { maximumFractionDigits: 1 });
  if (value > 0) {
    return `+${abs}`;
  }
  if (value < 0) {
    return `-${abs}`;
  }
  return abs;
}

function branchCoverStatus(status: ClinicRetentionSummary['status']): CoverBranchStatus {
  if (status === 'critical') return 'review';
  if (status === 'alert' || status === 'observation') return 'attention';
  return 'ok';
}

function branchCoverStatusClass(status: CoverBranchStatus): string {
  if (status === 'review') return 'ds-tone-critical';
  if (status === 'attention') return 'ds-tone-warning';
  return 'ds-tone-up';
}

function trendArrow(delta: number | null, flatBand = 0.25): 'up' | 'down' | 'flat' {
  if (delta === null || Math.abs(delta) < flatBand) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

function formatDeltaDisplay(
  locale: string,
  delta: number | null,
  kind: 'pct' | 'pts' | 'pp'
): { text: string; tone: SignalTone; warn: boolean } {
  if (delta === null) {
    return { text: '—', tone: 'flat', warn: false };
  }
  const signed = formatSigned(locale, delta);
  if (kind === 'pct') {
    return {
      text: `${signed}%`,
      tone: toneFromHigherBetter(delta),
      warn: delta <= -5,
    };
  }
  if (kind === 'pp') {
    return {
      text: `${signed} pp`,
      tone: toneFromLowerBetter(delta),
      warn: delta >= 2,
    };
  }
  return {
    text: `${signed} pts`,
    tone: toneFromHigherBetter(delta, 0.3),
    warn: delta <= -0.4,
  };
}

interface BranchMainMetrics {
  sede: Sede;
  summary: MetricsSummary | null;
  pvp: PvpMetrics | null;
  professionals: ProfesionalesMetrics | null;
}

/**
 * Hub intermedio de una clínica: KPI del período + acceso a reportes con scope fijado.
 */
export function ClinicHubPage() {
  const { tenantId = '' } = useParams<{ tenantId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { hasPermission } = usePermissions();
  const clinicNames = useClinicNames();
  const locale = i18n.language;
  const { period, year, month, periodQuery, previousPeriodQuery, label: periodLabel } = useGlobalPeriod();
  const parsed = { year, month };
  const tabParam = searchParams.get('tab');
  const activeTab: ClinicTab =
    tabParam === 'professionals' || tabParam === 'patients' || tabParam === 'finances'
      ? tabParam
      : 'overview';
  const sedeFromQuery = searchParams.get('sede') ?? searchParams.get('consultorioId') ?? '';
  const [sedeId, setSedeId] = useState<string>(() => (sedeFromQuery ? sedeFromQuery : 'all'));

  const setTab = useCallback(
    (tab: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === 'overview') {
            next.delete('tab');
          } else {
            next.set('tab', tab);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSede = useCallback(
    (nextSede: string) => {
      setSedeId(nextSede);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('consultorioId');
          if (nextSede === 'all' || nextSede === '') {
            next.delete('sede');
          } else {
            next.set('sede', nextSede);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (sedeFromQuery && sedeFromQuery !== sedeId) {
      setSedeId(sedeFromQuery);
    }
    if (!sedeFromQuery && sedeId !== 'all') {
      // URL sin sede → toda la clínica
      setSedeId('all');
    }
  }, [sedeFromQuery, sedeId]);

  const consultorioId = useMemo(() => {
    if (sedeId === 'all' || sedeId === '') return undefined;
    const n = Number(sedeId);
    return Number.isFinite(n) ? n : undefined;
  }, [sedeId]);
  const isSedeScoped = consultorioId !== undefined;

  const [summary, setSummary] = useState<MetricsSummary | null>(null);
  const [previousSummary, setPreviousSummary] = useState<MetricsSummary | null>(null);
  const [pvp, setPvp] = useState<PvpMetrics | null>(null);
  const [clinicProfessionals, setClinicProfessionals] = useState<ProfesionalesMetrics | null>(null);
  const [retention, setRetention] = useState<ClinicRetentionSummary | null>(null);
  const [riskTotal, setRiskTotal] = useState<number | null>(null);
  const [branchMainMetrics, setBranchMainMetrics] = useState<BranchMainMetrics[]>([]);
  const [sedes, setSedes] = useState<Sede[]>([]);
  const [isBranchMetricsLoading, setIsBranchMetricsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kpisOpen, setKpisOpen] = useState(false);
  const [branchesOpen, setBranchesOpen] = useState(false);

  const inScope = Boolean(user?.scope.allowedTenantIds.includes(tenantId));
  const canViewHub = canAccessClinicHub(hasPermission);
  const canFetchRisk = hasPermission('metrics.no_volvieron');
  const clinicLabel = clinicNames[tenantId] ?? tenantId;
  const tenantIdsMulti = user?.scope.allowedTenantIds?.length ?? 0;
  const sedeOptions = useMemo(
    () => sedes.map((s) => ({ value: String(s.id), label: s.nombre })),
    [sedes]
  );
  const showSedeSelector = sedeOptions.length > 1;

  useEffect(() => {
    if (!showSedeSelector) return;
    if (sedeId === 'all') return;
    const ok = sedes.some((s) => String(s.id) === sedeId);
    if (!ok) setSede('all');
  }, [sedes, sedeId, showSedeSelector, setSede]);

  useEffect(() => {
    if (tenantId && year && month) {
      persistReportScope(period, tenantId);
    }
  }, [tenantId, period, year, month]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !tenantId || !year || !month || !inScope) {
      setSummary(null);
      setPreviousSummary(null);
      setPvp(null);
      setClinicProfessionals(null);
      setRetention(null);
      setRiskTotal(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    const monthScope = { tenantId, year, month, consultorioId };
    const summaryScope = { tenantId, consultorioId, ...periodQuery };
    const prevSummaryScope = { tenantId, consultorioId, ...previousPeriodQuery };
    Promise.allSettled([
      getMetricsSummary(token, summaryScope),
      getMetricsSummary(token, prevSummaryScope),
      getPvpMetrics(token, monthScope),
      getProfesionalesMetrics(token, monthScope),
      getOwnerPvpRetentionDashboard(token, { year, month, months: 6 }),
      canFetchRisk && !isSedeScoped
        ? getPacientesNoVolvieronResumen(token, { tenantId, year, month })
        : Promise.resolve(null),
    ])
      .then(([summaryResult, previousResult, pvpResult, professionalsResult, retentionResult, riskResult]) => {
        if (cancelled) {
          return;
        }

        if (summaryResult.status === 'fulfilled') {
          setSummary(summaryResult.value);
        } else {
          setSummary(null);
          const reason = summaryResult.reason;
          setError(t('metrics.states.error.message'));
        }

        if (previousResult.status === 'fulfilled') {
          setPreviousSummary(previousResult.value);
        } else {
          setPreviousSummary(null);
        }

        if (pvpResult.status === 'fulfilled') {
          setPvp(pvpResult.value);
        } else {
          setPvp(null);
        }

        if (professionalsResult.status === 'fulfilled') {
          setClinicProfessionals(professionalsResult.value);
        } else {
          setClinicProfessionals(null);
        }

        if (retentionResult.status === 'fulfilled') {
          const clinicRetention = retentionResult.value.clinics.find((clinic) => clinic.tenantId === tenantId) ?? null;
          setRetention(clinicRetention);
        } else {
          setRetention(null);
        }

        if (riskResult.status === 'fulfilled' && riskResult.value) {
          setRiskTotal(riskResult.value.total);
        } else {
          setRiskTotal(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, tenantId, year, month, periodQuery, previousPeriodQuery, inScope, t, canFetchRisk, consultorioId, isSedeScoped]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !tenantId || !year || !month || !inScope) {
      setBranchMainMetrics([]);
      setSedes([]);
      return;
    }

    setIsBranchMetricsLoading(true);
    Promise.resolve()
      .then(async () => {
        let loadedSedes: Sede[] = [];
        try {
          loadedSedes = await getSedes(token, tenantId);
        } catch {
          loadedSedes = [];
        }
        if (cancelled) return;
        setSedes(loadedSedes);

        const rows = await Promise.all(
          loadedSedes.map(async (sede) => {
            const [summaryResult, pvpResult, professionalsResult] = await Promise.allSettled([
              getMetricsSummary(token, { tenantId, consultorioId: sede.id, ...periodQuery }),
              getPvpMetrics(token, { tenantId, year, month, consultorioId: sede.id }),
              getProfesionalesMetrics(token, { tenantId, year, month, consultorioId: sede.id }),
            ]);

            return {
              sede,
              summary: summaryResult.status === 'fulfilled' ? summaryResult.value : null,
              pvp: pvpResult.status === 'fulfilled' ? pvpResult.value : null,
              professionals: professionalsResult.status === 'fulfilled' ? professionalsResult.value : null,
            } satisfies BranchMainMetrics;
          })
        );

        if (!cancelled) {
          setBranchMainMetrics(rows.sort((a, b) => a.sede.nombre.localeCompare(b.sede.nombre)));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSedes([]);
          setBranchMainMetrics([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsBranchMetricsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, tenantId, year, month, periodQuery, inScope]);

  const cover = useMemo(() => {
    if (!summary) return null;

    const revDelta = previousSummary
      ? relativeChangePct(summary.caja.ingresos, previousSummary.caja.ingresos)
      : null;
    const volDelta = previousSummary
      ? relativeChangePct(summary.pacientes.atendidos, previousSummary.pacientes.atendidos)
      : null;
    const absDelta = previousSummary
      ? Number((summary.turnos.rates.absenteeism - previousSummary.turnos.rates.absenteeism).toFixed(1))
      : null;
    const pvpDelta = retention?.clinicPvpVariacionAbs ?? null;
    const pvpValue = retention?.clinicPvp ?? pvp?.value ?? null;
    const prosAlert = retention?.professionalsInAttention ?? 0;

    const branchRetention =
      consultorioId != null
        ? (retention?.branches ?? []).find((b) => b.consultorioId === consultorioId) ?? null
        : null;
    const signalRetentionStatus = branchRetention?.status ?? retention?.status;
    const signalPvpValue = branchRetention?.branchPvp ?? pvpValue;
    const signalPvpDelta = branchRetention?.branchPvpVariacionAbs ?? pvpDelta;
    const signalProsAlert = branchRetention?.professionalsInAttention ?? prosAlert;

    const revDisplay = formatDeltaDisplay(locale, revDelta, 'pct');
    const volDisplay = formatDeltaDisplay(locale, volDelta, 'pct');
    const absDisplay = formatDeltaDisplay(locale, absDelta, 'pp');

    const kpis = [
      {
        id: 'revenue',
        label: t('clinicHub.cover.kpis.revenue'),
        value: formatMoneyCompact(locale, summary.caja.ingresos),
        delta: revDisplay,
      },
      {
        id: 'patients',
        label: t('clinicHub.cover.kpis.patients'),
        value: formatInteger(summary.pacientes.atendidos, locale),
        delta: volDisplay,
      },
      {
        id: 'pvp',
        label: t('clinicHub.cover.kpis.pvp'),
        value:
          signalPvpValue === null
            ? '—'
            : signalPvpValue.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        delta: formatDeltaDisplay(locale, signalPvpDelta, 'pts'),
      },
      {
        id: 'absenteeism',
        label: t('clinicHub.cover.kpis.absenteeism'),
        value: formatPercent(summary.turnos.rates.absenteeism, locale),
        delta: absDisplay,
      },
    ];

    const rawSignals = buildHubTopSignals({
      revenueDeltaPct: revDelta,
      patientsDeltaPct: volDelta,
      absenteeismRate: summary.turnos.rates.absenteeism,
      absenteeismDeltaPp: absDelta,
      retentionStatus: signalRetentionStatus,
      pvpValue: signalPvpValue,
      pvpDeltaPts: signalPvpDelta,
      professionalsInAttention: signalProsAlert,
      riskPatientsTotal: riskTotal,
      includeRisk: canFetchRisk && !isSedeScoped,
    });

    const topSignals: CoverTopSignal[] = rawSignals
      .map((signal) => {
        const preferred = resolveAccessibleAttentionCta(signal.cta, hasPermission);
        if (!preferred || !canAccessAttentionCta(preferred, hasPermission)) {
          return null;
        }
        const href = buildAttentionHref({
          cta: preferred,
          tenantId,
          period,
          patientsTab: signal.patientsTab,
          sede: consultorioId,
        });

        if (signal.id === 'retention') {
          const pvpPart =
            signal.metrics.pvpValue == null
              ? ''
              : t('clinicHub.cover.alerts.retention.pvpLine', {
                  value: signal.metrics.pvpValue.toLocaleString(locale, { maximumFractionDigits: 1 }),
                  delta:
                    signal.metrics.pvpDeltaPts == null
                      ? t('clinicHub.cover.noCompare')
                      : `${formatSigned(locale, signal.metrics.pvpDeltaPts)} pts`,
                });
          const prosPart =
            (signal.metrics.prosInAttention ?? 0) > 0
              ? t('clinicHub.cover.alerts.retention.prosLine', {
                  count: signal.metrics.prosInAttention,
                })
              : '';
          return {
            ...signal,
            cta: preferred,
            title: t('clinicHub.cover.alerts.retention.title'),
            body: [pvpPart, prosPart].filter(Boolean).join(' · '),
            ctaLabel: t('clinicHub.cover.alerts.retention.cta'),
            href,
          };
        }

        if (signal.id === 'revenue' || signal.id === 'revenue_critical') {
          return {
            ...signal,
            cta: preferred,
            title: t('clinicHub.cover.alerts.revenue.title'),
            body: t('clinicHub.cover.alerts.revenue.body', {
              value: formatSigned(locale, signal.metrics.revenueDeltaPct ?? 0),
            }),
            ctaLabel: t('clinicHub.cover.alerts.revenue.cta'),
            href,
          };
        }

        if (signal.id === 'patients_volume') {
          return {
            ...signal,
            cta: preferred,
            title: t('clinicHub.cover.alerts.patients.title'),
            body: t('clinicHub.cover.alerts.patients.body', {
              value: formatSigned(locale, signal.metrics.patientsDeltaPct ?? 0),
            }),
            ctaLabel: t('clinicHub.cover.alerts.patients.cta'),
            href,
          };
        }

        if (signal.id === 'absenteeism') {
          return {
            ...signal,
            cta: preferred,
            title: t('clinicHub.cover.alerts.absenteeism.title'),
            body: t('clinicHub.cover.alerts.absenteeism.body', {
              value: (signal.metrics.absenteeismRate ?? 0).toLocaleString(locale, {
                maximumFractionDigits: 1,
              }),
            }),
            ctaLabel: t('clinicHub.cover.alerts.absenteeism.cta'),
            href,
          };
        }

        return {
          ...signal,
          cta: preferred,
          title: t('clinicHub.topSignals.risk.title'),
          body: t('clinicHub.topSignals.risk.body', { count: signal.metrics.riskCount ?? 0 }),
          ctaLabel: t('clinicHub.topSignals.risk.cta'),
          href,
        };
      })
      .filter((s): s is CoverTopSignal => s !== null);

    const retentionByBranch = new Map((retention?.branches ?? []).map((b) => [b.consultorioId, b]));
    const branchRows = branchMainMetrics
      .filter((row) => (consultorioId == null ? true : row.sede.id === consultorioId))
      .map((row) => {
        const ret = retentionByBranch.get(row.sede.id);
        const status = ret ? branchCoverStatus(ret.status) : 'ok';
        const pvpBranch = row.pvp?.value ?? ret?.branchPvp ?? null;
        const pvpBranchDelta = ret?.branchPvpVariacionAbs ?? null;
        return {
          id: row.sede.id,
          name: row.sede.nombre,
          status,
          revenue: row.summary?.caja.ingresos ?? null,
          pvp: pvpBranch,
          trend: trendArrow(pvpBranchDelta),
          sortSeverity: status === 'review' ? 0 : status === 'attention' ? 1 : 2,
          sortRevenue: row.summary?.caja.ingresos ?? 0,
        };
      })
      .sort((a, b) => a.sortSeverity - b.sortSeverity || b.sortRevenue - a.sortRevenue);

    return {
      kpis,
      topSignals,
      branches: branchRows,
      /** Total de sedes de la clínica (para mostrar sección), no el filtro. */
      branchesTotal: branchMainMetrics.length,
    };
  }, [
    summary,
    previousSummary,
    retention,
    pvp,
    branchMainMetrics,
    locale,
    t,
    riskTotal,
    canFetchRisk,
    hasPermission,
    tenantId,
    period,
    consultorioId,
    isSedeScoped,
  ]);

  const openOnDashboard = () => {
    navigate(`/?period=${period}`);
  };

  const canExportSummary = canExportReports(hasPermission);
  const handleExportSummary = useCallback(
    (format: ReportFormat) => {
      if (!year || !month || !tenantId) {
        return Promise.reject(new Error('export_scope_missing'));
      }
      return downloadSummaryReport(
        token ?? '',
        { tenantId, year, month, consultorioId },
        format,
        {
          clinicName: clinicLabel,
          filename: t('clinicHub.exportSummary.filename', {
            clinic: clinicLabel,
            period,
          }),
        }
      );
    },
    [year, month, tenantId, token, consultorioId, clinicLabel, period, t]
  );

  const tabs = useMemo(() => {
    const all: Array<{ id: ClinicTab; label: string; show: boolean }> = [
      {
        id: 'overview',
        label: t('clinicHub.tabs.overview'),
        show: hasPermission('dashboard.view') || canViewHub,
      },
      {
        id: 'professionals',
        label: t('clinicHub.tabs.professionals'),
        show: hasPermission('metrics.profesionales'),
      },
      {
        id: 'patients',
        label: t('clinicHub.tabs.patients'),
        show:
          hasPermission('metrics.pacientes') ||
          hasPermission('metrics.recurrencia') ||
          hasPermission('metrics.no_volvieron'),
      },
      {
        id: 'finances',
        label: t('clinicHub.tabs.finances'),
        show:
          hasPermission('metrics.caja') ||
          hasPermission('metrics.tendencia') ||
          hasPermission('metrics.ranking_planes'),
      },
    ];
    return all.filter((tab) => tab.show).map(({ id, label }) => ({ id, label }));
  }, [t, hasPermission, canViewHub]);

  useEffect(() => {
    if (tabs.length === 0) return;
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setTab(tabs[0].id);
    }
  }, [tabs, activeTab, setTab]);

  if (!inScope) {
    return (
      <div className="space-y-4 p-4 xl:p-6">
        <MetricsState
          variant="empty"
          title={t('clinicHub.outOfScope.title')}
          message={t('clinicHub.outOfScope.message')}
        />
        <Link
          to={firstVisibleHousePath(hasPermission) ?? '/'}
          className="panel-link text-sm font-semibold hover:underline"
        >
          {t('clinicHub.backDashboard')}
        </Link>
      </div>
    );
  }

  if (!canViewHub) {
    return <AccessDenied domain="clinics" homeTo={firstVisibleHousePath(hasPermission)} />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="panel-chrome shrink-0 space-y-4 px-4 pb-3 pt-4 xl:px-6 xl:pt-6">
        <Breadcrumbs
          label={t('breadcrumbs.nav')}
          items={[
            { label: t('breadcrumbs.clinics'), to: breadcrumbHref('/clinics', { period }) },
            { label: clinicLabel },
          ]}
        />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="panel-eyebrow">
              {t('clinicHub.eyebrow')}
            </p>
            <h1 className="panel-page-title mt-1">{clinicLabel}</h1>
            <p className="panel-page-subtitle">
              {t('clinicHub.periodLabel', { period: periodLabel })}
            </p>
            <p className="panel-question mt-1">{t('clinicHub.question')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canExportSummary ? (
              <div data-testid="clinic-hub-export-summary">
                <ExportButtons
                  label={t('clinicHub.exportSummary.label')}
                  onExport={handleExportSummary}
                  compact
                  touchFriendly
                />
              </div>
            ) : null}
            <button
              type="button"
              onClick={openOnDashboard}
              className="panel-btn-secondary"
            >
              {t('clinicHub.openDashboard')}
            </button>
            <Link
              to={tenantIdsMulti > 1 ? `/clinics?period=${period}` : `/?period=${period}`}
              className="panel-btn-ghost"
            >
              {tenantIdsMulti > 1 ? t('clinicHub.backClinics') : t('clinicHub.backDashboard')}
            </Link>
          </div>
        </div>

        <DomainScopeBar
          showSede={showSedeSelector}
          sedeId={sedeId}
          sedeOptions={sedeOptions}
          onSedeChange={setSede}
        />

        <DomainTabs
          tabs={tabs}
          active={activeTab}
          onChange={setTab}
          ariaLabel={t('clinicHub.tabsLabel')}
        />
      </div>

      <div
        className={`min-h-0 flex-1 px-4 py-5 xl:px-6 ${
          activeTab === 'professionals'
            ? 'flex flex-col overflow-y-auto overscroll-contain lg:overflow-hidden'
            : 'overflow-y-auto overscroll-contain'
        }`}
      >
      {isLoading && <PageSkeleton variant="domain" data-testid="clinic-hub-loading" />}
      {!isLoading && error && (
        <PanelPageState variant="error" layout="inline" onRetry={() => window.location.reload()} />
      )}
      {activeTab === 'overview' && !isLoading && !error && !cover && (
        <PanelPageState variant="empty" layout="inline" message={t('metrics.states.empty.message')} />
      )}

      {/* Resumen: Top 3 primero; resto colapsado — no reordenar señales (#21 / Sofia). */}
      {activeTab === 'overview' && !isLoading && !error && cover && (
        <div className="space-y-8 pb-10 pt-1">
          {isSedeScoped ? (
            <p
              className="panel-note-warning"
              data-testid="clinic-hub-sede-scope-note"
            >
              {t('clinicHub.sedeScopeNote')}
            </p>
          ) : null}
          {/* 1. Top 3 a mirar */}
          <section className="space-y-4" data-testid="clinic-hub-top-signals">
            <h2 className="panel-section-title tracking-[0.14em]">
              {t('clinicHub.topSignals.title')}
            </h2>
            {cover.topSignals.length === 0 ? (
              <div className="max-w-xl space-y-2" data-testid="clinic-hub-top-signals-healthy">
                <p className="ds-tone-up text-base font-semibold">
                  {t('clinicHub.topSignals.healthyTitle')}
                </p>
                <p className="panel-body text-sm">{t('clinicHub.topSignals.healthyBody')}</p>
                <ul className="panel-body list-disc space-y-1 pl-5 text-sm">
                  <li>{t('clinicHub.topSignals.tipFinances')}</li>
                  <li>{t('clinicHub.topSignals.tipPatients')}</li>
                </ul>
              </div>
            ) : (
              <ul className="max-w-xl space-y-5">
                {cover.topSignals.map((signal) => (
                  <li key={signal.id} className="space-y-1" data-testid={`clinic-hub-signal-${signal.id}`}>
                    <p className="metric-hero-banner__phrase text-lg font-semibold sm:text-xl">
                      <span
                        className={`mr-1.5 ${
                          signal.severity === 'critical' ? 'ds-tone-critical' : 'ds-tone-warning'
                        }`}
                        aria-hidden
                      >
                        ⚠
                      </span>
                      {signal.title}
                    </p>
                    {signal.body ? (
                      <p className="panel-body text-sm">{signal.body}</p>
                    ) : null}
                    <Link
                      to={signal.href}
                      className="panel-link inline-block text-sm font-semibold hover:underline"
                      data-testid={`clinic-hub-signal-cta-${signal.id}`}
                    >
                      {signal.ctaLabel} →
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* 2. Estado del mes — colapsado por defecto */}
          <section className="space-y-4">
            <button
              type="button"
              onClick={() => setKpisOpen((v) => !v)}
              className="flex w-full max-w-xl items-center justify-between gap-2 text-left"
              aria-expanded={kpisOpen}
              data-testid="clinic-hub-kpis-toggle"
            >
              <h2 className="panel-section-title tracking-[0.14em]">
                <span className="mr-1.5 inline-block w-3 text-[10px]" aria-hidden>
                  {kpisOpen ? '▼' : '▶'}
                </span>
                {t(periodQuery.fromMonth !== undefined ? 'clinicHub.cover.thisYear' : 'clinicHub.cover.thisMonth')}
              </h2>
              <span className="panel-link text-xs font-semibold">
                {kpisOpen ? t('clinicHub.professionalsTab.collapse') : t('clinicHub.professionalsTab.expand')}
              </span>
            </button>
            {kpisOpen ? (
              <MetricPrimaryStrip
                className="grid-cols-2 lg:grid-cols-4 xl:grid-cols-4"
                data-testid="clinic-hub-kpis-panel"
                aria-label={t(periodQuery.fromMonth !== undefined ? 'clinicHub.cover.thisYear' : 'clinicHub.cover.thisMonth')}
              >
                {cover.kpis.map((kpi) => (
                  <MetricKpiCard
                    key={kpi.id}
                    variant="primary"
                    data-kpi-id={kpi.id}
                    label={kpi.label}
                    value={kpi.value}
                    tone={
                      kpi.id === 'revenue'
                        ? 'green'
                        : kpi.id === 'pvp'
                          ? 'indigo'
                          : kpi.id === 'absenteeism'
                            ? 'amber'
                            : 'slate'
                    }
                    metricId={isMetricDefinitionId(kpi.id) ? kpi.id : undefined}
                    detail={
                      <span className={toneClass(kpi.delta.tone)}>
                        {kpi.delta.warn ? <span className="ds-tone-warning mr-1" aria-hidden>⚠</span> : null}
                        {kpi.delta.tone === 'flat'
                          ? kpi.delta.text === '—'
                            ? '—'
                            : `— ${t('clinicHub.cover.stable')}`
                          : `${kpi.delta.tone === 'up' ? '↑' : '↓'} ${kpi.delta.text}`}
                      </span>
                    }
                  />
                ))}
              </MetricPrimaryStrip>
            ) : null}
          </section>

          {/* 3. Sucursales — colapsado por defecto */}
          {cover.branchesTotal > 1 ? (
            <section className="space-y-4">
              <button
                type="button"
                onClick={() => setBranchesOpen((v) => !v)}
                className="flex w-full max-w-xl items-center justify-between gap-2 text-left"
                aria-expanded={branchesOpen}
                data-testid="clinic-hub-branches-toggle"
              >
                <h2 className="panel-section-title tracking-[0.14em]">
                  <span className="mr-1.5 inline-block w-3 text-[10px]" aria-hidden>
                    {branchesOpen ? '▼' : '▶'}
                  </span>
                  {t('clinicHub.cover.branches')}
                </h2>
                <span className="panel-link text-xs font-semibold">
                  {branchesOpen
                    ? t('clinicHub.professionalsTab.collapse')
                    : t('clinicHub.professionalsTab.expand')}
                </span>
              </button>
              {branchesOpen ? (
                isBranchMetricsLoading ? (
                  <div className="panel-soft-card max-w-2xl">
                    <LoadingBlock size="sm" minHeightClassName="min-h-[5rem]" />
                  </div>
                ) : (
                  <div className="metric-secondary-panel max-w-2xl" data-testid="clinic-hub-branches-panel">
                    <table className="min-w-full text-left text-sm">
                      <thead>
                        <tr>
                          <th className="pb-2 pr-4 font-semibold">{t('clinicHub.cover.branchCols.branch')}</th>
                          <th className="pb-2 pr-4 font-semibold">{t('clinicHub.cover.branchCols.status')}</th>
                          <th className="pb-2 pr-4 font-semibold">{t('clinicHub.cover.branchCols.revenue')}</th>
                          <th className="pb-2 pr-4 font-semibold">{t('clinicHub.cover.branchCols.pvp')}</th>
                          <th className="pb-2 font-semibold">{t('clinicHub.cover.branchCols.trend')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cover.branches.map((branch) => (
                          <tr key={branch.id} className="border-t border-[var(--color-border)]">
                            <td className="panel-heading py-2.5 pr-4 font-semibold">{branch.name}</td>
                            <td className={`py-2.5 pr-4 font-medium ${branchCoverStatusClass(branch.status)}`}>
                              {t(`clinicHub.cover.branchStatus.${branch.status}`)}
                            </td>
                            <td className="panel-body py-2.5 pr-4 tabular-nums">
                              {branch.revenue === null ? '—' : formatMoneyCompact(locale, branch.revenue)}
                            </td>
                            <td className="panel-body py-2.5 pr-4 tabular-nums">
                              {branch.pvp === null
                                ? '—'
                                : branch.pvp.toLocaleString(locale, { maximumFractionDigits: 1 })}
                            </td>
                            <td className="panel-body py-2.5 tabular-nums">
                              {branch.trend === 'up' ? '↑' : branch.trend === 'down' ? '↓' : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : null}
            </section>
          ) : null}
        </div>
      )}

      {activeTab === 'professionals' && !isLoading && !error && (
        <div className="min-h-0 flex-1">
          <ClinicHubProfessionalsPanel
            tenantId={tenantId}
            period={period}
            clinicProfessionals={clinicProfessionals}
            pvp={pvp}
            retention={retention}
            branchMainMetrics={branchMainMetrics}
            isBranchMetricsLoading={isBranchMetricsLoading}
            hubSedeId={consultorioId ?? null}
          />
        </div>
      )}

      {activeTab === 'patients' && !isLoading && !error && parsed && (
        <ClinicHubPatientsPanel
          tenantId={tenantId}
          period={period}
          year={parsed.year}
          month={parsed.month}
          summary={summary}
          previousSummary={previousSummary}
          branchMainMetrics={branchMainMetrics}
          isBranchMetricsLoading={isBranchMetricsLoading}
          consultorioId={consultorioId}
        />
      )}

      {activeTab === 'finances' && !isLoading && !error && summary && parsed && (
        <ClinicHubFinancesPanel
          tenantId={tenantId}
          period={period}
          year={parsed.year}
          month={parsed.month}
          summary={summary}
          previousSummary={previousSummary}
          branchMainMetrics={branchMainMetrics}
          isBranchMetricsLoading={isBranchMetricsLoading}
          hubSedeId={consultorioId ?? null}
        />
      )}
      </div>
    </div>
  );
}
